/* ==========================================================================
   ApnaCam Infinite Scroll v5 — auto-loads all collection products on scroll
   FULLY SELF-CONTAINED: this file builds the water-wave logo loader itself
   (markup + styles + animation) inside the #ApnaCamLoader div, so the
   section file only needs the empty loader div and the sentinel.
   - Theme-independent paging (?page=2, ?page=3 ...)
   - Dual trigger: IntersectionObserver + scroll-position fallback
   - Water-wave animation via Web Animations API (immune to theme CSS)
   - Compatible with filters/sorting (facets.js)
   ========================================================================== */
(() => {
  'use strict';

  // Minimum time (ms) the loader stays visible per batch.
  const MIN_LOADER_MS = 1500;

  // How close (px) the end of products must be to the screen bottom
  // before the next batch starts loading.
  const TRIGGER_DISTANCE = 150;

  const SELECTORS = {
    container: '#ProductGridContainer',
    grid: '#product-grid',
    sentinel: '#ApnaCamScrollSentinel',
    loader: '#ApnaCamLoader',
    itemLink: 'a[href*="/products/"]',
  };

  const log = (...args) => console.log('[ApnaCam Infinite Scroll]', ...args);

  /* ---------- Self-contained loader UI ---------- */

  function iconUrl() {
    // The icon lives in the same /assets/ folder as this script
    const s = document.querySelector('script[src*="apnacam-infinite-scroll"]');
    if (!s) return null;
    return s.src.replace(/apnacam-infinite-scroll\.js.*$/, 'apnacam-loader-icon.png');
  }

  function injectLoaderStyles(icon) {
    if (document.getElementById('ApnaCamLoaderStyles')) return;
    const style = document.createElement('style');
    style.id = 'ApnaCamLoaderStyles';
    style.textContent = [
      '#ApnaCamLoader{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:4rem 0 2rem;}',
      '#ApnaCamLoader[hidden]{display:none;}',
      '.acam-liquid{position:relative;width:64px;height:64px;overflow:hidden;',
      'background:rgba(0,0,0,0.12);',
      "-webkit-mask-image:url('" + icon + "');-webkit-mask-size:contain;-webkit-mask-position:center;-webkit-mask-repeat:no-repeat;",
      "mask-image:url('" + icon + "');mask-size:contain;mask-position:center;mask-repeat:no-repeat;}",
      '.acam-rise{position:absolute;left:0;top:0;width:100%;height:100%;transform:translateY(105%);}',
      '.acam-water{position:absolute;left:50%;top:0;width:130px;height:130px;margin-left:-65px;background:#111;border-radius:42%;}',
    ].join('');
    document.head.appendChild(style);
  }

  function buildLoaderUI(loader) {
    const icon = iconUrl();
    if (!icon) {
      log('warning: could not derive icon URL - loader will be invisible');
      return null;
    }
    injectLoaderStyles(icon);
    loader.innerHTML =
      '<div class="acam-liquid" aria-hidden="true">' +
      '<div class="acam-rise"><div class="acam-water"></div></div>' +
      '</div>' +
      '<span class="visually-hidden">Loading more products</span>';
    return {
      rise: loader.querySelector('.acam-rise'),
      water: loader.querySelector('.acam-water'),
    };
  }

  /* ---------- Infinite scroll ---------- */

  class ApnaCamInfiniteScroll {
    constructor() {
      this.container = document.querySelector(SELECTORS.container);
      this.sentinel = document.querySelector(SELECTORS.sentinel);
      this.loader = document.querySelector(SELECTORS.loader);
      if (!this.container || !this.sentinel || !this.loader) {
        console.warn('[ApnaCam Infinite Scroll] loader/sentinel/container missing - check section markup');
        return;
      }

      this.ui = buildLoaderUI(this.loader);
      this.animations = null;
      this.loading = false;
      this.finished = false;
      this.page = this.currentPageFromUrl();

      this.io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) this.maybeLoad('observer');
        },
        { rootMargin: TRIGGER_DISTANCE + 'px 0px' }
      );
      this.io.observe(this.sentinel);

      let ticking = false;
      this.onScrollOrResize = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.maybeLoad('scroll');
        });
      };
      window.addEventListener('scroll', this.onScrollOrResize, { passive: true });
      window.addEventListener('resize', this.onScrollOrResize, { passive: true });

      this.mo = new MutationObserver(() => {
        this.page = this.currentPageFromUrl();
        this.finished = false;
        this.loading = false;
        this.hideLoader();
        log('grid re-rendered (filter/sort) - paging reset to page', this.page);
      });
      this.mo.observe(this.container, { childList: true, subtree: false });

      log('v5 ready - current page:', this.page, '- loader UI:', this.ui ? 'built' : 'FAILED');
    }

    currentPageFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const p = parseInt(params.get('page'), 10);
      return Number.isFinite(p) && p > 0 ? p : 1;
    }

    buildNextUrl() {
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(this.page + 1));
      return url.toString();
    }

    sentinelNearViewport() {
      const grid = document.querySelector(SELECTORS.grid);
      const ref = grid || this.sentinel;
      const rect = ref.getBoundingClientRect();
      return rect.bottom < window.innerHeight + TRIGGER_DISTANCE;
    }

    maybeLoad(source) {
      if (this.loading || this.finished) return;
      if (!this.sentinelNearViewport()) return;
      this.loadNextPage(source);
    }

    existingProductHrefs() {
      const grid = document.querySelector(SELECTORS.grid);
      const hrefs = new Set();
      if (!grid) return hrefs;
      grid.querySelectorAll(SELECTORS.itemLink).forEach((a) => {
        hrefs.add(a.href.split('?')[0]);
      });
      return hrefs;
    }

    showLoader() {
      this.loader.removeAttribute('hidden');
      this.startLoaderAnimation();
    }

    hideLoader() {
      this.loader.setAttribute('hidden', '');
      this.stopLoaderAnimation();
    }

    startLoaderAnimation() {
      if (!this.ui || !this.ui.rise || typeof this.ui.rise.animate !== 'function') return;
      if (this.animations) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        this.ui.rise.style.transform = 'translateY(0)';
        this.animations = [
          this.ui.rise.animate([{ opacity: 1 }, { opacity: 0.35 }, { opacity: 1 }], {
            duration: 1400,
            iterations: Infinity,
            easing: 'ease-in-out',
          }),
        ];
        return;
      }

      this.animations = [
        // Water level rises bottom to top, brief hold when full, loops
        this.ui.rise.animate(
          [
            { transform: 'translateY(105%)', offset: 0 },
            { transform: 'translateY(-15%)', offset: 0.85 },
            { transform: 'translateY(-15%)', offset: 1 },
          ],
          { duration: 2400, iterations: Infinity, easing: 'ease-in-out' }
        ),
        // Rotating rounded square = rolling wave surface
        this.ui.water.animate(
          [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
          { duration: 3000, iterations: Infinity, easing: 'linear' }
        ),
      ];
    }

    stopLoaderAnimation() {
      if (this.animations) {
        this.animations.forEach((a) => a.cancel());
        this.animations = null;
      }
    }

    async loadNextPage(source) {
      if (this.loading || this.finished) return;
      this.loading = true;
      this.showLoader();
      const startedAt = Date.now();
      log('loading page', this.page + 1, '(trigger: ' + (source || 'manual') + ')');

      try {
        const response = await fetch(this.buildNextUrl());
        if (!response.ok) throw new Error('Fetch failed: ' + response.status);
        const html = await response.text();

        const elapsed = Date.now() - startedAt;
        if (elapsed < MIN_LOADER_MS) {
          await new Promise((resolve) => setTimeout(resolve, MIN_LOADER_MS - elapsed));
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const currentGrid = document.querySelector(SELECTORS.grid);
        const newItems = Array.from(doc.querySelectorAll(SELECTORS.grid + ' > li'));

        if (!currentGrid || newItems.length === 0) {
          this.finished = true;
          log('no more products - done');
          return;
        }

        const existing = this.existingProductHrefs();
        let appended = 0;
        newItems.forEach((li) => {
          const link = li.querySelector(SELECTORS.itemLink);
          const key = link ? link.href.split('?')[0] : null;
          if (key && existing.has(key)) return;
          li.classList.remove('scroll-trigger', 'animate--slide-in');
          li.removeAttribute('data-cascade');
          li.style.removeProperty('--animation-order');
          currentGrid.appendChild(document.importNode(li, true));
          appended++;
        });

        if (appended === 0) {
          this.finished = true;
          log('all remaining products were duplicates - done');
        } else {
          this.page += 1;
          log('appended', appended, 'products - now on page', this.page);
        }
      } catch (err) {
        console.error('[ApnaCam Infinite Scroll]', err);
        this.finished = true;
      } finally {
        this.loading = false;
        this.hideLoader();
        if (!this.finished && this.sentinelNearViewport()) {
          this.loadNextPage('chain');
        }
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new ApnaCamInfiniteScroll());
  } else {
    new ApnaCamInfiniteScroll();
  }
})();